/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, HFlow, Modal, ModalBody, ModalFooter, ModalSidebar, Text, useTheme, VFlow } from 'bold-ui'
import { FooterButton } from 'components/footer-button'
import { Form, FormRenderProps, usePecField } from 'components/form'
import { IconLabel } from 'components/IconLabel'
import { confirm } from 'components/modals/confirm'
import { NavigationButton } from 'components/route/NavigationButton'
import { useFlags } from 'config/useFlagsContext'
import { FormState } from 'final-form'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { MetaPath } from 'util/metaPath'

import { MChatMarca } from '../../components/MChatMarca'
import { MCHAT_PERGUNTAS, MCHAT_URL_MODAL, MChatAplicacaoModel, MChatFieldModel } from '../model-mchat'
import { calcularResultadoMChat } from '../utils-mchat'
import { mChatValidator } from '../validator-mchat'
import { MChatModalStepper, MChatModalStepperStatus } from './MChatModalStepper'
import { MChatModalTabAdicionar } from './MChatModalTabAdicionar'
import { MChatModalTabResultado } from './MChatModalTabResultado'

const questionsEntries = Object.entries(MCHAT_PERGUNTAS)
const numberOfQuestions = questionsEntries.length

enum MChatModalTab {
  APLICACAO = 'APLICACAO',
  RESULTADO = 'RESULTADO',
}

interface MChatModalProps {
  name: MetaPath<MChatFieldModel>
  atendProfId: ID
  cidadaoId: ID
  isEditing: boolean
  modalConsultaSeguimentoUrl: string
  onSave: (values: MChatFieldModel) => void
  onClose?: () => void
}

export function MChatModal(props: Readonly<MChatModalProps>) {
  const { name, atendProfId, cidadaoId, isEditing, modalConsultaSeguimentoUrl, onSave, onClose } = props

  const { M_CHAT_RF_ENABLED } = useFlags()
  const theme = useTheme()
  const history = useHistory()
  const match = useRouteMatch()
  const { analytics } = useFirebase()
  const {
    input: { value },
  } = usePecField({ name, subscription: { value: true } })

  const hasValues = !isEmpty(value)
  const shouldAvaliarConsultaSeguimento = hasValues && value.risco === MChatRiscoEnum.MEDIO && M_CHAT_RF_ENABLED
  const initialStepperStatus =
    !hasValues || isEditing
      ? 'respondendo'
      : shouldAvaliarConsultaSeguimento
      ? 'finalizado-com-seguimento'
      : 'finalizado'

  const [currentTab, setCurrentTab] = useState<MChatModalTab>(
    hasValues && !isEditing ? MChatModalTab.RESULTADO : MChatModalTab.APLICACAO
  )
  const [numberOfAnswers, setNumberOfAnswers] = useState(hasValues ? Object.keys(value.aplicacao).length : 0)
  const [stepperStatus, setStepperStatus] = useState<MChatModalStepperStatus>(initialStepperStatus)
  const [answeringTimeStart, setAnsweringTimeStart] = useState<number>()
  const [wentBackToAplicacao, setWentBackToAplicacao] = useState<boolean>()

  const isAllAnswered = numberOfAnswers === numberOfQuestions

  const handleSubmit = (values: MChatAplicacaoModel) => {
    if (!isEditing && !wentBackToAplicacao) {
      const currentTime = new Date().getTime()
      const answeringTimeEndInSec = Math.floor((currentTime - answeringTimeStart) / 1000)
      analytics.logEvent('registro_novo_mchatr', {
        tempo: answeringTimeEndInSec,
      })
    }
    analytics.logEvent('click_calcular_mchatr')

    const calculatedValues = calcularResultadoMChat(values)
    onSave(calculatedValues)
    setStepperStatus(calculatedValues.risco === MChatRiscoEnum.MEDIO ? 'finalizado-com-seguimento' : 'finalizado')
    setCurrentTab(MChatModalTab.RESULTADO)
  }

  const handleFormChange = ({ values }: FormState<MChatAplicacaoModel>) => {
    setNumberOfAnswers(Object.keys(values).length)
  }

  const goBack = () => history.push(match.url.split(MCHAT_URL_MODAL)[0])

  const handleClose = () => {
    if (currentTab === MChatModalTab.APLICACAO) {
      mChatConfirmDialog(isEditing, () => {
        analytics.logEvent('click_cancelar_mchatr')
        onClose?.()
        goBack()
      })
    } else {
      onClose?.()
      goBack()
    }
  }

  const handleGoBackToAplicacao = () => {
    setCurrentTab(MChatModalTab.APLICACAO)
    setStepperStatus('respondendo')
    setWentBackToAplicacao(true)
  }

  useEffect(() => {
    setAnsweringTimeStart(new Date().getTime())
  }, [])

  const renderModal = (renderProps: FormRenderProps<MChatAplicacaoModel>) => {
    return (
      <Modal open closeOnBackdropClick={false} onClose={handleClose} style={styles.modal}>
        <ModalSidebar position='left' style={styles.sidebar}>
          <VFlow style={styles.sidebarContent}>
            <MChatModalStepper status={stepperStatus} numberOfAnswers={numberOfAnswers} />
            <MChatMarca />
          </VFlow>
        </ModalSidebar>
        <ModalBody>
          {currentTab === MChatModalTab.APLICACAO ? (
            <MChatModalTabAdicionar isEditing={isEditing} handleFormChange={handleFormChange} />
          ) : (
            <MChatModalTabResultado
              values={value}
              atendProfId={atendProfId}
              cidadaoId={cidadaoId}
              isEditing={isEditing}
              onClickVoltar={handleGoBackToAplicacao}
            />
          )}
        </ModalBody>

        <ModalFooter>
          <HFlow justifyContent='flex-end' alignItems='center'>
            {currentTab === MChatModalTab.RESULTADO && (
              <IconLabel
                icon='checkCircleOutline'
                text='Resultado salvo no atendimento'
                color={theme.pallete.status.success.c40}
              />
            )}

            <FooterButton onClick={handleClose}>
              {currentTab === MChatModalTab.RESULTADO ? 'Fechar' : 'Cancelar'}
            </FooterButton>

            {currentTab === MChatModalTab.APLICACAO ? (
              <Button
                kind='primary'
                onClick={renderProps.handleSubmit}
                disabled={!isAllAnswered}
                data-testid='calcularResultadoMChat'
              >
                {isEditing ? 'Recalcular' : 'Calcular'} resultado
              </Button>
            ) : (
              shouldAvaliarConsultaSeguimento && (
                <NavigationButton kind='primary' to={modalConsultaSeguimentoUrl}>
                  Avaliar entrevista de seguimento
                </NavigationButton>
              )
            )}
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<MChatAplicacaoModel>
      initialValues={value.aplicacao}
      render={renderModal}
      validate={mChatValidator}
      onSubmit={handleSubmit}
    />
  )
}

const mChatConfirmDialog = (isEditing: boolean, handleConfirm: () => void) => {
  confirm({
    title: `Deseja sair sem ${isEditing ? 'recalcular' : 'calcular'} o resultado?`,
    icon: 'exclamationTriangleOutline',
    confirmLabel: 'Sair da tela',
    cancelLabel: 'Continuar aqui',
    type: 'danger',
    size: 'auto',
    onConfirm: handleConfirm,
    render: () => <Text>As alterações realizadas na edição serão perdidas.</Text>,
  })()
}

const styles = {
  modal: css`
    width: 62rem;
    max-width: 62rem;
    overflow: hidden;
  `,
  hFlow: css`
    max-height: inherit;
    grid-auto-columns: 17rem auto;
  `,
  sidebar: css`
    padding-top: 3.75rem;
    padding-left: 2.5rem;
    padding-right: 2rem;
    padding-bottom: 1rem;
  `,
  sidebarContent: css`
    height: 100%;
    justify-content: space-between;
  `,
}
